/**
 * Utility functions for correctly counting floors in floor plans
 * 
 * CRITICAL: Only count floors that have actual wall data, not just floor keys.
 * Creating extensions/highlights can create floor keys without walls, causing
 * incorrect floor counts in dimensions/cavity wall insulation pages.
 */

/**
 * Gets the actual number of floors that have walls
 * 
 * @param floorPlans - The floorPlans object from floor plan data
 * @returns Array of floor numbers that actually have walls
 */
export function getFloorsWithWalls(floorPlans: Record<number, any[]> | undefined): number[] {
  if (!floorPlans) {
    return [];
  }

  // Get all floor numbers and filter to only those with actual walls
  return Object.keys(floorPlans)
    .map(Number)
    .filter(floorNum => !isNaN(floorNum))
    .filter(floorNum => {
      const walls = floorPlans[floorNum];
      return Array.isArray(walls) && walls.length > 0;
    })
    .sort((a, b) => a - b);
}

/**
 * Counts the number of floors that have walls
 * 
 * @param floorPlans - The floorPlans object from floor plan data
 * @returns Number of floors with actual walls
 */
export function countFloorsWithWalls(floorPlans: Record<number, any[]> | undefined): number {
  return getFloorsWithWalls(floorPlans).length;
}

/**
 * Gets the actual number of floors from allFloorData structure
 * Only counts floors that have actual room/wall data
 * 
 * @param allFloorData - The allFloorData object structure
 * @returns Array of floor numbers that have data
 */
export function getFloorsWithDataFromAllFloorData(allFloorData: Record<number, any> | undefined): number[] {
  if (!allFloorData) {
    return [];
  }

  return Object.keys(allFloorData)
    .map(Number)
    .filter(floorNum => !isNaN(floorNum))
    .filter(floorNum => {
      const floorData = allFloorData[floorNum];
      // Check if floor has meaningful data (rooms or metrics)
      return floorData && (
        (floorData.rooms && floorData.rooms.length > 0) ||
        (floorData.floorMetrics && floorData.floorMetrics.totalArea > 0)
      );
    })
    .sort((a, b) => a - b);
}

/**
 * Counts floors from allFloorData structure
 * 
 * @param allFloorData - The allFloorData object structure  
 * @returns Number of floors with actual data
 */
export function countFloorsFromAllFloorData(allFloorData: Record<number, any> | undefined): number {
  return getFloorsWithDataFromAllFloorData(allFloorData).length;
}
